/* rd-bookings — one shared booking-card family. Compact preserves the Home
   density; roomy is confirmed Frame 7 / Big card A2 for the Bookings tab.
   Colors and effects resolve only through the injected client-theme tokens. */

.bookingCard {
  position: relative;
  width: 100%;
  appearance: none;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: stretch;
  margin-bottom: 10px;
  padding: 0;
  border-radius: 14px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--booking-card-shadow);
  overflow: hidden;
  text-align: left;
  font-family: var(--font-body);
}
.bookingCard:last-child {
  margin-bottom: 0;
}
.bookingCard:active {
  transform: scale(0.99);
}
.bookingCard:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
/* tapered glass ring (mockup booking-card::before) */
.bookingCard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--booking-card-ring-bg);
  -webkit-mask: linear-gradient(currentColor 0 0) content-box, linear-gradient(currentColor 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.bookingCard > * {
  position: relative;
  z-index: 1;
}
.bookingImage {
  width: 88px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
}
.roomy .bookingImage {
  width: 104px;
}
.bookingImage::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, transparent 60%, var(--bg-vignette) 100%);
  opacity: 0.25;
}
.bookingBody {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 4px 10px 12px;
}
.roomy .bookingBody {
  gap: 4px;
  padding: 14px 4px 14px 14px;
}
.typeKicker {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 3px;
  color: var(--accent-hot);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 1;
  text-transform: uppercase;
}
.typeKicker svg {
  width: 10px;
  height: 10px;
  stroke-width: 2;
  flex-shrink: 0;
}
.bookingTitle {
  margin: 0 0 4px;
  min-width: 0;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 15.5px;
  letter-spacing: -0.022em;
  color: var(--text-primary);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.roomy .bookingTitle {
  margin-bottom: 0;
  font-size: 17.5px;
  letter-spacing: -0.02em;
  line-height: 1.15;
}
.providerLine {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 11.5px;
}
.providerLine b {
  color: var(--text-primary);
  font-weight: 500;
}
.providerAvatar {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  overflow: hidden;
  background: var(--avatar-bg);
  background-position: center;
  background-size: cover;
  box-shadow: inset 0 0 0 1px var(--glass-border-strong);
  color: var(--avatar-initials-color);
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
}
.bookingMeta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.roomy .bookingMeta {
  gap: 3px;
  margin-top: 3px;
}
.bookingMetaRow {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
  min-width: 0;
  white-space: nowrap;
}
.roomy .bookingMetaRow {
  font-size: 11.5px;
}
.bookingMetaRow svg {
  width: 11px;
  height: 11px;
  stroke-width: 1.8;
  color: var(--text-tertiary);
  flex-shrink: 0;
}
.roomy .bookingMetaRow svg {
  width: 11.5px;
  height: 11.5px;
}
.metaText {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.locationText {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.eventMeta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  color: var(--accent-hot);
  font-weight: 600;
  white-space: nowrap;
}
.eventMeta svg {
  color: currentColor;
}
.chevronSlot {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  padding: 0 12px 0 6px;
}
.bookingChev {
  width: 14px;
  height: 14px;
  stroke-width: 2.4;
  color: var(--chevron-color);
  flex-shrink: 0;
}

/* ── Status pill (shared accent treatment for every booking state) ── */
.statusPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 11px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.005em;
  white-space: nowrap;
  background: var(--status-pill-bg);
  color: var(--status-pill-text);
  flex-shrink: 0;
}
/* Photo-less fallback: keep the previously shipped date-row placement. */
.dateRow .statusPill {
  margin-left: auto;
  padding: 3px 8px;
  font-size: 10px;
}
/* Frame 7 A2: every photographed card floats the pill on the image. */
.bookingImage .statusPill {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  margin: 0;
  padding: 4px 9px;
  background: var(--glass-bg-strong);
  box-shadow: inset 0 0 0 1px var(--glass-border-strong);
  backdrop-filter: var(--glass-blur);
  font-size: 9.5px;
}
/* PENDING_PAYMENT — hot accent + hairline ring + clock glyph */
.statusPillPayment {
  background: rgba(var(--accent-hot-rgb), 0.18);
  color: var(--accent-hot);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.35);
}
.statusPillPayment svg {
  width: 10px;
  height: 10px;
  stroke-width: 2;
}
/* ── Balance chip (DEPOSIT_PAID) — rides the location row, pushed right ── */
.paymentRow {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  font-size: 10.5px;
  font-weight: 500;
  color: var(--accent-hot);
  flex-shrink: 0;
  white-space: nowrap;
}
.paymentRow svg {
  width: 11px;
  height: 11px;
  stroke-width: 1.8;
  color: var(--accent);
  flex-shrink: 0;
}
